import React, { useState } from 'react';
import * as consts from 'config/consts';
import { useModal } from 'hooks/useModal';
import {
  getDataFetchingState,
  isSimple,
} from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import { PositionItemsProps } from 'components/features/home/ProfitAndLossStuffing/ProfitAndLossStuffing';
import {
  isGoSymbolDetail as isGoSymbolDetail_,
  amountToAmountColor,
} from 'components/features/utils/util';
import {
  AssetTypeKey,
  assetTypeKeyToDotColor,
  assetTypeKeyToString,
  AssetTypesLabel,
} from 'components/features/utils/assetType';
import { DailyReportWhatIsIncludeAsstsModal } from 'components/features/modal/DailyReportWhatIsIncludeAsstsModal';
import { DataFetching } from 'components/features/home/PortfolioStuffing/asset/AssetOrganizedPortfolioItem';
import { AssetHistoryItemsProps } from 'components/features/home/assetStuffing/AssetStuffing';
import { productTypeToAssetTypeKey } from 'components/features/utils/productTypes';
import {
  commaFormat,
  addSignCommaFormat,
  addSignCommaFormatPercentage,
} from '../../utils/formatter';

const getproductName = (key: AssetTypeKey): string => {
  switch (key) {
    case consts.AssetType.国内現物:
      return 'stockAmount';
    case consts.AssetType.ロボアドバイザー:
      return 'roboAdvisorAmount';
    case consts.AssetType.外国株式:
      return 'uSStockAmount';
    case consts.AssetType.投資信託:
      return 'investmentTrustAmount';
    case consts.AssetType.未上場株式:
      return 'unlistedStockAmount';
    case consts.AssetType['国内ETF・ETN']:
      return 'etfEtnAmount';
    case consts.AssetType['外国ETF・ETN']:
      return 'foreignEtfEtnAmount';
    case consts.AssetType.国内REIT:
      return 'reitAmount';
    case consts.AssetType.外国REIT:
      return 'foreignReitAmount';
    case consts.AssetType.国内債券:
      return 'bondAmount';
    case consts.AssetType.外国債券:
      return 'foreignBondAmount';
    case consts.AssetType.国内EB:
      return 'ebAmount';
    case consts.AssetType.外国EB:
      return 'foreignEbAmount';
    case consts.AssetType.不動産:
      return 'realEstateAmount';
    case consts.AssetType.預金_MRF:
      return 'cashAmount';
    case consts.AssetType.その他:
    default:
      return 'otherAmount';
  }
};

const PositionItem = ({
  position,
  assetType,
  onTradeItemClick: onTradeItemClick_,
}: {
  position: PositionItemsProps;
  assetType: AssetTypeKey;
  onTradeItemClick: (symbol: string) => void;
}) => {
  type DataFetchingState = 'Fetching' | 'Fetched';
  const dataFetchingState: DataFetchingState = getDataFetchingState(
    position.symbol,
    position.productType === consts.productTypes.投資信託
      ? consts.AssetType['投資信託']
      : consts.AssetType['その他']
  );

  const isGoSymbolDetail =
    isGoSymbolDetail_(position.symbol, position.productType) &&
    dataFetchingState !== 'Fetching';

  const getDataFetchingTextStyle = (defaultTextStyle: string): string =>
    dataFetchingState === 'Fetching' ? 'text-bluegray-300' : defaultTextStyle;

  const isDataCooperation =
    assetType === consts.AssetType['投資信託'] &&
    !isSimple(position.symbol) &&
    position.symbol !== null &&
    /^MONO/.test(position.symbol);
  const isAnalogyComplete =
    assetType === consts.AssetType['投資信託'] &&
    !isSimple(position.symbol) &&
    position.symbol !== null &&
    (position.symbol === '' || /^DC/.test(position.symbol));

  const isAlert =
    position.diffProfitOrLoss === null &&
    (position.status === '100' || position.status === '101');

  const isDisable = position.status === '101' || isDataCooperation;
  const disableColor = 'text-bluegray-300';

  const onTradeItemClick = (symbol: string): void => {
    if (!isGoSymbolDetail || isDataCooperation || isAnalogyComplete) return;
    onTradeItemClick_(symbol);
  };

  return (
    <div
      className={`flex flex-col mx-2 py-4 pl-2 border-t ${
        isGoSymbolDetail ? 'cursor-pointer' : ''
      }`}
      onClick={(): void => onTradeItemClick(position.symbol)}>
      {isDataCooperation && (
        <>
          <DataFetching className="mb-3" />
        </>
      )}
      <div className="flex flex-row items-center justify-between">
        <div className="w-full flex flex-col">
          <div className="flex flex-row">
            <div className="w-5">
              {isAlert && <div className="icon-alert h-5 text-red"></div>}
            </div>
            <AssetTypesLabel assetType={assetType} className={''} />
            <div
              className={`ml-2 text-xs ${
                isDataCooperation ? disableColor : 'text-bluegray-500'
              }`}>
              {position.symbol}
            </div>
          </div>
          <div
            className={`mt-1 ml-6 text-sm font-bold line-clamp-2 ${
              isDisable ? disableColor : ''
            } ${getDataFetchingTextStyle('')}`}>
            {position.symbolName}
          </div>
          <div
            className={`mt-1 ml-auto text-base font-bold ${
              isDisable ? disableColor : ''
            }`}>
            {position.validationAmount === null
              ? '---'
              : commaFormat(position.validationAmount, { numberOfDecimals: 0 })}
            <span className="ml-1 text-sm">円</span>
          </div>
          <div className="flex flex-row item-center ml-6">
            <div
              className={`text-xs ${
                isDataCooperation ? disableColor : 'text-bluegray-500'
              }`}>
              前日比
            </div>
            {position.diffProfitOrLoss === null ||
            position.diffProfitOrLossRate === null ? (
              <div
                className={`ml-auto text-xs ${
                  isDataCooperation ? disableColor : 'text-bluegray-700'
                }`}>
                ----
              </div>
            ) : (
              <div
                className={`ml-auto text-xs ${
                  isDataCooperation
                    ? disableColor
                    : getDataFetchingTextStyle(
                        position.diffProfitOrLoss === null
                          ? ''
                          : amountToAmountColor(position.diffProfitOrLoss)
                      )
                }`}>
                {addSignCommaFormat(position.diffProfitOrLoss)}円 (
                {addSignCommaFormat(position.diffProfitOrLossRate, {
                  numberOfDecimals: 2,
                })}
                %)
              </div>
            )}
          </div>
        </div>
        <div className="w-4 mx-2">
          {isGoSymbolDetail ? (
            <div className="icon-chevron-right text-2xl text-gray-500" />
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

const PositionListInternal = ({
  assetType,
  amount,
  manualAmount,
  diffAmount,
  diffAmountRate,
  targetPositions,
  onTradeItemClick,
}: {
  assetType: AssetTypeKey;
  amount: number;
  manualAmount: number;
  diffAmount: number;
  diffAmountRate: number;
  targetPositions: PositionItemsProps[];
  onTradeItemClick: (symbol: string) => void;
}) => {
  const [isShowDetail, setIsShowDetail] = useState<boolean>(false);
  const [isShowModal, { hideModal, showModal }] = useModal();

  const color = assetTypeKeyToDotColor(assetType);

  const targetAssetTypes = [
    consts.AssetType.預金_MRF,
    consts.AssetType.その他,
  ] as number[];
  const isShowModalLink = targetAssetTypes.includes(assetType);
  const isNotShowDetailAssetType = targetAssetTypes
    .concat([consts.AssetType.ロボアドバイザー] as number[])
    .includes(assetType);

  return (
    <>
      <div
        className={`flex flex-row pt-4 px-2 ${isShowModalLink ? '' : 'pb-4'} ${
          isNotShowDetailAssetType ? '' : 'cursor-pointer'
        } border-t`}
        onClick={() => setIsShowDetail(prev => !prev)}>
        <div className="w-full flex flex-col">
          <div className="flex flex-row items-center justify-between">
            <div
              className="w-full grid items-center pl-2"
              style={{ gridTemplateColumns: 'auto auto 1fr' }}>
              <div
                className={`w-4 h-4 rounded-full ${color} mr-2`}
                style={{ gridColumn: '1', gridRow: '1' }}></div>
              <div
                className="text-base font-bold"
                style={{ gridColumn: '2', gridRow: '1' }}>
                {assetTypeKeyToString(assetType)}
              </div>
              <div
                className="ml-auto text-lg font-bold"
                style={{ gridColumn: '3', gridRow: '1' }}>
                {commaFormat(amount, { numberOfDecimals: 0 })}
                <span className="text-base ml-1">円</span>
              </div>
              <div
                className="text-xs text-bluegray-500"
                style={{ gridColumn: '2', gridRow: '2' }}>
                前日比
              </div>
              <div
                className={`ml-auto text-xs ${amountToAmountColor(diffAmount)}`}
                style={{ gridColumn: '3', gridRow: '2' }}>
                {addSignCommaFormat(diffAmount)}円 (
                {addSignCommaFormatPercentage(diffAmountRate, {
                  numberOfDecimals: 2,
                })}
                %)
              </div>
            </div>
          </div>
          {isShowModalLink ? (
            <div className="flex pt-2 pb-4 text-bluegray-500">
              <div
                className="flex flex-row items-center ml-auto cursor-pointer"
                onClick={showModal}>
                <div className="text-xs mr-1">
                  {assetTypeKeyToString(assetType)}について
                </div>
                <div className="icon-help_outline text-sm"></div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="w-4 mx-2 flex items-center">
          {!isNotShowDetailAssetType && (
            <div
              className={`icon-chevron-${
                isShowDetail ? 'up' : 'down'
              } text-2xl text-gray-500`}></div>
          )}
        </div>
      </div>
      {!isNotShowDetailAssetType && (
        <>
          {isShowDetail && (
            <>
              {targetPositions.map((position, i) => (
                <React.Fragment key={i}>
                  <PositionItem
                    {...{
                      position,
                      assetType,
                      onTradeItemClick,
                    }}
                  />
                </React.Fragment>
              ))}
              {manualAmount !== 0 ? (
                <div className="flex flex-row mx-2 py-4 pl-2 border-t">
                  <div className="w-full flex flex-col">
                    <div className="flex flex-row">
                      <div className="w-5"></div>
                      <AssetTypesLabel assetType={assetType} className={''} />
                    </div>
                    <div className="mt-1 ml-6 text-sm font-bold line-clamp-2">
                      直接入力
                      {assetType === consts.AssetType.国内現物
                        ? ' / Moneytree連携の一部資産'
                        : ''}
                    </div>
                    <div className="mt-1 ml-auto text-base font-bold">
                      {commaFormat(manualAmount, { numberOfDecimals: 0 })}
                      <span className="ml-1 text-sm">円</span>
                    </div>
                    {assetType === consts.AssetType.国内現物 ? (
                      <div className="flex mt-2 text-bluegray-500">
                        <div
                          className="flex flex-row items-center ml-auto cursor-pointer"
                          onClick={showModal}>
                          <div className="text-xs mr-1">この資産について</div>
                          <div className="icon-help_outline text-sm"></div>
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                  <div className="w-4 mx-2" />
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        </>
      )}
      <DailyReportWhatIsIncludeAsstsModal
        {...{
          isShowModal,
          hideModal,
          assetTypeKey: assetType,
        }}
      />
    </>
  );
};

const PositionListProduct = ({
  assetType,
  positions,
  assetsHistory,
  onTradeItemClick,
}: PositionListProps) => {
  const name = getproductName(assetType);
  const targetAssetsHistory = assetsHistory
    .map(list => {
      return { day: list.day, amount: list[name as string] };
    })
    .sort((first, second) => {
      return first.day > second.day ? -1 : 1;
    });

  if (
    targetAssetsHistory.every(list => list.amount === 0 || list.amount === null)
  )
    return <></>;

  // 合計
  const amount = targetAssetsHistory[0].amount;
  const previousAmount = targetAssetsHistory[1].amount;
  const diffAmount = amount - previousAmount;
  const diffAmountRate = diffAmount / previousAmount;

  if (amount === 0) return <></>;

  // 詳細
  const targetPositions = positions
    .filter(list => {
      const assetTypeKey = productTypeToAssetTypeKey(list);
      return assetTypeKey === assetType ? list : null;
    })
    .filter(x => x);

  // 詳細-直接入力
  const sumPositionAmount = targetPositions.reduce((acc, current) => {
    const num = current.validationAmount;
    return acc + (num ?? 0);
  }, 0);
  const manualAmount = amount - sumPositionAmount;

  return (
    <PositionListInternal
      {...{
        assetType,
        amount,
        manualAmount,
        diffAmount,
        diffAmountRate,
        targetPositions,
        onTradeItemClick,
      }}
    />
  );
};

const PositionListMargin = ({
  assetType,
  positions,
  assetsHistory,
  onTradeItemClick,
}: PositionListProps) => {
  const marginList = assetsHistory
    .map(asset => {
      // memo: 手入力の信用はassetsHistoryのmarginAmountからMONO連携口座の信用の合算を引いた値とする。
      // 信用-MONO
      const total =
        (asset.stockAmount ?? 0) +
        (asset.marginAmount ?? 0) +
        (asset.marginProfitOrLoss ?? 0) +
        (asset.roboAdvisorAmount ?? 0) +
        (asset.uSStockAmount ?? 0) +
        (asset.investmentTrustAmount ?? 0) +
        (asset.unlistedStockAmount ?? 0) +
        (asset.etfEtnAmount ?? 0) +
        (asset.foreignEtfEtnAmount ?? 0) +
        (asset.reitAmount ?? 0) +
        (asset.foreignReitAmount ?? 0) +
        (asset.bondAmount ?? 0) +
        (asset.foreignBondAmount ?? 0) +
        (asset.ebAmount ?? 0) +
        (asset.foreignEbAmount ?? 0) +
        (asset.realEstateAmount ?? 0) +
        (asset.cashAmount ?? 0) +
        (asset.otherAmount ?? 0);
      const marginMONO = total - (asset.totalAssetAmount ?? 0);

      // 信用-手入力
      const marginManual = (asset.marginAmount ?? 0) - marginMONO;

      // 信用-手入力とassetsHistoryの信用の合算値
      const marginTotal = (asset.marginProfitOrLoss ?? 0) + marginManual;

      return { day: asset.day, marginTotal, marginManual };
    })
    .sort((first, second) => {
      return first.day > second.day ? -1 : 1;
    });

  if (
    marginList.every(
      list => list.marginTotal === 0 || list.marginTotal === null
    )
  )
    return <></>;

  // 合計
  const latestMarginList = marginList[0];
  const previousMarginList = marginList[1];
  const diffMargin =
    latestMarginList.marginTotal - previousMarginList.marginTotal;
  const diffMarginRate = diffMargin / previousMarginList.marginTotal;

  // 詳細
  const marginPositions = positions
    .filter(list => {
      const assetTypeKey = productTypeToAssetTypeKey(list);
      return assetTypeKey === assetType ? list : null;
    })
    .filter(x => x);

  return (
    <PositionListInternal
      {...{
        assetType,
        amount: latestMarginList.marginTotal,
        manualAmount: latestMarginList.marginManual,
        diffAmount: diffMargin,
        diffAmountRate: diffMarginRate,
        targetPositions: marginPositions,
        onTradeItemClick,
      }}
    />
  );
};

export type PositionListProps = {
  assetType: AssetTypeKey;
  positions: PositionItemsProps[];
  assetsHistory: AssetHistoryItemsProps[];
  onTradeItemClick: (symbol: string) => void;
};

export const PositionList = ({
  assetType,
  positions,
  assetsHistory,
  onTradeItemClick,
}: PositionListProps) => {
  if (assetType === consts.AssetType.信用) {
    return (
      <PositionListMargin
        {...{
          assetType,
          positions,
          assetsHistory,
          onTradeItemClick,
        }}
      />
    );
  } else {
    return (
      <PositionListProduct
        {...{ assetType, positions, assetsHistory, onTradeItemClick }}
      />
    );
  }
};
